import { useCallback, useMemo, useRef, useState } from "react";
import { formatMoney } from "../../utils/money";
import { categoryIcon } from "../../utils/expenseCategories";

// The Expenses page as charts: one filter row scopes everything below it.
// Colors (validated on the white card surface): one brand green for single
// series; when a category is picked, that category stays green and the rest
// goes to a neutral gray (emphasis). Money in is violet, which clears the
// colorblind checks against the green (orange does not). Every chart has a
// table view, and every mark shows its value on hover and keyboard focus.

const MARK = "#2F8A57";
const REST = "#c9c7bd";
const MONEY_IN = "#6250d6";
const INK = "#2D2D2D"; // birdie-charcoal, for axis text and hairlines in SVG
const BIG_CENTS = 500000; // "big purchases": $5,000 and up (the bus, the Trackman)

const RANGES = [
  { key: "all", label: "All time" },
  { key: "year", label: "This year" },
  { key: "90", label: "Last 90 days" },
];

function localDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}
const DAY = 86400000;
const cents = (e) => Number(e.amount_cents) || 0;
const usd0 = (c) => `${c < 0 ? "-" : ""}$${Math.abs(Math.round(c / 100)).toLocaleString()}`;
function compact(c) {
  const v = Math.abs(c / 100);
  const s = v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${+(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}K` : `${Math.round(v)}`;
  return `${c < 0 ? "-" : ""}$${s}`;
}
function niceMax(v) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

// A callback ref, so the chart keeps measuring after the table view swaps it out and back.
function useWidth() {
  const [w, setW] = useState(0);
  const ro = useRef(null);
  const ref = useCallback((el) => {
    ro.current?.disconnect();
    ro.current = null;
    if (!el) return;
    setW(el.clientWidth);
    if ("ResizeObserver" in window) {
      ro.current = new ResizeObserver(([e]) => setW(e.contentRect.width));
      ro.current.observe(el);
    }
  }, []);
  return [ref, w];
}

export default function ExpenseCharts({ data, onOpenExpense, onShowCategory }) {
  const [range, setRange] = useState("all");
  const [hideBig, setHideBig] = useState(false);
  const [focus, setFocus] = useState(""); // a category to pick out

  const s = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paid = data.expenses.filter((e) => e.status === "paid");
    const firstDated = paid.reduce((m, e) => {
      const d = localDate(e.spent_on);
      return d && (!m || d < m) ? d : m;
    }, null);
    const start =
      range === "year" ? new Date(today.getFullYear(), 0, 1) : range === "90" ? new Date(today - 89 * DAY) : firstDated || today;
    const inRange = (d) => (range === "all" ? true : d && d >= start);
    const big = paid.filter((e) => cents(e) >= BIG_CENTS && inRange(localDate(e.spent_on)));
    const rows = paid.filter((e) => inRange(localDate(e.spent_on)) && !(hideBig && cents(e) >= BIG_CENTS));
    const dated = rows.filter((e) => e.spent_on);
    const total = rows.reduce((t, e) => t + cents(e), 0);

    const group = (key) => {
      const m = new Map();
      for (const e of rows) {
        const k = key(e);
        if (!k) continue;
        const g = m.get(k) || { key: k, total: 0, count: 0, sample: e };
        g.total += cents(e);
        g.count += 1;
        m.set(k, g);
      }
      return [...m.values()].sort((a, b) => b.total - a.total);
    };
    const categories = group((e) => e.category || "Other");
    const accounts = group((e) => e.account_name || "Card not set");
    const vendors = group((e) => (e.vendor || "").trim());

    // Months from the start of the range to now, empty months included.
    const months = [];
    const m0 = new Date(start.getFullYear(), start.getMonth(), 1);
    for (let d = m0; d <= today && months.length < 24; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
      months.push({ key: monthKey(d), label: d.toLocaleDateString([], { month: "short" }),
                    long: d.toLocaleDateString([], { month: "long", year: "numeric" }), total: 0, part: 0 });
    }
    const byKey = new Map(months.map((x) => [x.key, x]));
    for (const e of dated) {
      const x = byKey.get(monthKey(localDate(e.spent_on)));
      if (!x) continue;
      x.total += cents(e);
      if (focus && (e.category || "Other") === focus) x.part += cents(e);
    }

    const spanDays = Math.max(1, Math.round((today - start) / DAY) + 1);
    const perMonth = dated.reduce((t, e) => t + cents(e), 0) / Math.max(1, spanDays / 30.44);
    const biggest = [...rows].sort((a, b) => cents(b) - cents(a));

    // Running totals: spent, and (with big purchases in) partner money in.
    const spentEvents = dated.map((e) => ({ t: localDate(e.spent_on), v: cents(e) }));
    const inEvents = hideBig
      ? null
      : data.money_in.filter((m) => m.received_on && inRange(localDate(m.received_on))).map((m) => ({ t: localDate(m.received_on), v: cents(m) }));

    return {
      start, today, rows, big, total, categories, accounts, vendors, months, perMonth, biggest,
      undated: rows.length - dated.length, spentEvents, inEvents, spanDays,
    };
  }, [data, range, hideBig, focus]);

  const focusTotal = s.categories.find((c) => c.key === focus);
  const pick = (c) => setFocus((f) => (f === c ? "" : c));

  return (
    <div className="space-y-4">
      {/* One filter row; everything below follows it. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-xl bg-birdie-cream p-1" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              aria-pressed={range === r.key}
              className={`min-h-0 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
                range === r.key ? "bg-white text-birdie-charcoal shadow-sm" : "text-birdie-charcoal/55"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 rounded-xl border border-birdie-green/15 bg-white px-3 py-1.5 text-xs font-semibold text-birdie-charcoal/70 cursor-pointer">
          <input
            type="checkbox"
            checked={hideBig}
            onChange={(e) => setHideBig(e.target.checked)}
            className="h-3.5 w-3.5 min-h-0 min-w-0 accent-birdie-green"
          />
          Hide big purchases ($5k+)
        </label>
        {focus && (
          <button
            type="button"
            onClick={() => setFocus("")}
            className="min-h-0 rounded-xl bg-birdie-green/10 px-3 py-1.5 text-xs font-semibold text-birdie-green"
          >
            {categoryIcon(focus)} {focus} ✕
          </button>
        )}
      </div>
      {hideBig && s.big.length > 0 && (
        <p className="-mt-2 text-[11px] text-birdie-charcoal/50">
          Leaving out {s.big.length}: {s.big.map((e) => `${e.description} (${usd0(cents(e))})`).join(", ")}.
        </p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Tile label="Spent" value={formatMoney(s.total)} sub={`${s.rows.length} expense${s.rows.length === 1 ? "" : "s"}`} />
        <Tile
          label="Average month"
          value={usd0(s.perMonth)}
          sub={hideBig ? "Running costs" : "Includes big purchases"}
        />
        <Tile
          label="Biggest category"
          value={s.categories[0] ? `${categoryIcon(s.categories[0].key)} ${s.categories[0].key}` : "—"}
          sub={s.categories[0] ? `${usd0(s.categories[0].total)} · ${pct(s.categories[0].total, s.total)}` : ""}
          small
        />
        <Tile
          label="Biggest purchase"
          value={s.biggest[0] ? usd0(cents(s.biggest[0])) : "—"}
          sub={s.biggest[0]?.description || ""}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card
          title="Spending by month"
          sub={
            focus
              ? `${focus} against everything else`
              : "Pick a category under “Where it went” to see its share each month."
          }
        >
          <MonthColumns months={s.months} focus={focus} />
          <UndatedNote n={s.undated} />
        </Card>

        <Card
          title="Running total"
          sub={s.inEvents ? "Everything spent, against the money Tyler and Gabe put in" : "Everything spent in this range"}
        >
          <RunningTotal
            start={s.start}
            end={s.today}
            series={[
              { key: "spent", label: "Spent", color: MARK, events: s.spentEvents },
              ...(s.inEvents ? [{ key: "in", label: "Money in", color: MONEY_IN, events: s.inEvents }] : []),
            ]}
          />
          <UndatedNote n={s.undated} />
        </Card>

        <Card title="Where it went" sub="Tap a category to pick it out in the charts.">
          <BarList
            rows={s.categories}
            total={s.total}
            active={focus}
            onPick={pick}
            label={(c) => `${categoryIcon(c.key)} ${c.key}`}
            footer={
              focusTotal && (
                <button
                  type="button"
                  onClick={() => onShowCategory(focus)}
                  className="mt-2 text-xs font-semibold text-birdie-green"
                >
                  See the {focusTotal.count} {focus} expense{focusTotal.count === 1 ? "" : "s"} →
                </button>
              )
            }
          />
        </Card>

        <Card title="Paid from" sub="Which account or card paid. A partner's own card counts as money they put in.">
          <BarList
            rows={s.accounts}
            total={s.total}
            label={(a) => {
              const acct = data.accounts.find((x) => x.name === a.key);
              return `${acct?.kind === "card" ? "💳" : acct ? "🏦" : "❔"} ${a.key}${
                acct && acct.owner !== "company" ? ` (${acct.owner}'s)` : ""
              }`;
            }}
          />
        </Card>

        <Card title="Top vendors" sub="Where the money went most often.">
          <BarList rows={s.vendors.slice(0, 8)} total={s.total} label={(v) => v.key} showCount />
        </Card>

        <Card title="Biggest purchases">
          <ul className="divide-y divide-birdie-green/10 -mx-1">
            {s.biggest.slice(0, 8).map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => onOpenExpense(e.id)}
                  className="w-full flex items-center gap-3 px-1 py-2 text-left hover:bg-birdie-cream/50 rounded-lg"
                >
                  <span aria-hidden>{categoryIcon(e.category)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-birdie-charcoal truncate">{e.description}</span>
                    <span className="block text-[11px] text-birdie-charcoal/50 truncate">
                      {[e.spent_on && localDate(e.spent_on).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }),
                        e.vendor, e.account_name].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-birdie-charcoal">{formatMoney(cents(e))}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function UndatedNote({ n }) {
  if (!n) return null;
  return (
    <p className="mt-2 text-[11px] text-birdie-charcoal/45">
      {n} expense{n === 1 ? " has" : "s have"} no date yet, so {n === 1 ? "it isn't" : "they aren't"} on this chart.
    </p>
  );
}

const pct = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");

function Tile({ label, value, sub, small }) {
  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl px-3.5 py-3 min-w-0">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className={`${small ? "text-base leading-snug" : "text-xl truncate"} font-semibold text-birdie-charcoal mt-0.5`}>{value}</div>
      {sub && <div className="text-[11px] text-birdie-charcoal/50 truncate">{sub}</div>}
    </div>
  );
}

function Card({ title, sub, children }) {
  return (
    <section className="bg-white border border-birdie-green/15 rounded-2xl p-4 min-w-0">
      <div className="font-accent text-base text-birdie-charcoal">{title}</div>
      {sub && <p className="text-[11px] text-birdie-charcoal/50 mb-2">{sub}</p>}
      <div className={sub ? "" : "mt-2"}>{children}</div>
    </section>
  );
}

function TableToggle({ asTable, onToggle }) {
  return (
    <div className="flex justify-end -mt-1 mb-1">
      <button type="button" onClick={onToggle} className="min-h-0 text-[11px] font-semibold text-birdie-charcoal/50 hover:text-birdie-green">
        {asTable ? "Show chart" : "Show as table"}
      </button>
    </div>
  );
}

// Monthly columns. With a category picked, each column is that category (green,
// on the baseline) under everything else (gray), with a 2px gap between them.
function MonthColumns({ months, focus, height = 180 }) {
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const max = niceMax(Math.max(0, ...months.map((m) => m.total)));
  const ticks = [0, max / 2, max];
  const last = months.length - 1;

  if (asTable) {
    return (
      <>
        <TableToggle asTable onToggle={() => setAsTable(false)} />
        <table className="w-full text-sm">
          <caption className="sr-only">Spending by month</caption>
          <thead>
            <tr className="text-[11px] text-birdie-charcoal/50 text-left">
              <th className="font-medium py-1">Month</th>
              {focus && <th className="font-medium py-1 text-right">{focus}</th>}
              <th className="font-medium py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.key} className="border-t border-birdie-green/10">
                <td className="py-1.5 text-birdie-charcoal/70">{m.long}</td>
                {focus && <td className="py-1.5 text-right tabular-nums">{formatMoney(m.part)}</td>}
                <td className="py-1.5 text-right tabular-nums text-birdie-charcoal">{formatMoney(m.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <>
      <TableToggle asTable={false} onToggle={() => setAsTable(true)} />
      {focus && (
        <div className="flex gap-3 mb-2 text-[11px] text-birdie-charcoal/60">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: MARK }} /> {focus}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: REST }} /> Everything else
          </span>
        </div>
      )}
      <div className="flex gap-2" role="group" aria-label="Spending by month">
        <div className="relative w-10 shrink-0 text-[10px] text-birdie-charcoal/45 tabular-nums" style={{ height }}>
          {ticks.map((t) => (
            <div key={t} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - t / max) * 100}%` }}>
              {compact(t)}
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative" style={{ height }}>
            {ticks.map((t) => (
              <div key={t} className="absolute inset-x-0 border-t border-birdie-charcoal/10" style={{ top: `${(1 - t / max) * 100}%` }} />
            ))}
            <div className="absolute inset-0 flex items-end">
              {months.map((m, i) => {
                const total = Math.max(0, m.total);
                const part = focus ? Math.max(0, Math.min(m.part, total)) : total;
                const rest = total - part;
                const h = (total / max) * 100;
                const tip = focus
                  ? `${m.long}: ${formatMoney(m.part)} ${focus}, ${formatMoney(m.total)} total`
                  : `${m.long}: ${formatMoney(m.total)}`;
                return (
                  <button
                    key={m.key}
                    type="button"
                    aria-label={tip}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(i)}
                    onBlur={() => setHover(null)}
                    onClick={() => setHover(i)}
                    className="relative flex-1 min-w-0 h-full flex items-end justify-center focus:outline-none"
                  >
                    <span
                      className="flex w-[60%] max-w-[24px] flex-col transition-opacity"
                      style={{ height: `${h}%`, opacity: hover === null || hover === i ? 1 : 0.5 }}
                    >
                      {rest > 0 && (
                        <span
                          className={`block w-full rounded-t ${part > 0 ? "mb-[2px]" : ""}`}
                          style={{ flexGrow: rest, minHeight: 2, background: focus ? REST : MARK }}
                        />
                      )}
                      {part > 0 && (
                        <span
                          className={`block w-full ${rest > 0 ? "" : "rounded-t"}`}
                          style={{ flexGrow: part, minHeight: 2, background: MARK }}
                        />
                      )}
                    </span>
                    {i === last && hover === null && m.total > 0 && !focus && (
                      <span
                        className="absolute left-1/2 -translate-x-1/2 text-[10px] font-semibold text-birdie-charcoal whitespace-nowrap"
                        style={{ bottom: `calc(${h}% + 4px)` }}
                      >
                        {compact(m.total)}
                      </span>
                    )}
                    {hover === i && (
                      <span
                        className={`absolute z-10 ${
                          i < 2 ? "left-0" : i > last - 2 ? "right-0" : "left-1/2 -translate-x-1/2"
                        } rounded-lg bg-birdie-charcoal text-white text-[11px] px-2 py-1.5 whitespace-nowrap shadow-lg pointer-events-none text-left`}
                        style={{ bottom: `calc(${Math.min(h, 80)}% + 6px)` }}
                      >
                        <span className="block font-semibold">{formatMoney(m.total)}</span>
                        {focus && (
                          <span className="block">
                            <span className="font-semibold">{formatMoney(m.part)}</span> <span className="opacity-70">{focus}</span>
                          </span>
                        )}
                        <span className="block opacity-70">{m.long}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex mt-1">
            {months.map((m, i) => (
              <div key={m.key} className="flex-1 min-w-0 text-center text-[10px] text-birdie-charcoal/45 overflow-hidden whitespace-nowrap">
                {months.length > 12 && i % 2 ? "" : m.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// Running totals as 2px step lines (money moves in lumps, not slopes). A crosshair
// follows the pointer and the tooltip lists every series on that day.
function RunningTotal({ series, start, end, height = 200 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);

  const model = useMemo(() => {
    const t0 = start.getTime();
    const t1 = Math.max(end.getTime(), t0 + DAY);
    const days = new Set([t0, t1]);
    for (const s of series) for (const e of s.events) days.add(Math.max(t0, e.t.getTime()));
    const xs = [...days].sort((a, b) => a - b);
    const lines = series.map((s) => {
      const sorted = [...s.events].sort((a, b) => a.t - b.t);
      let run = 0;
      let j = 0;
      const vals = xs.map((t) => {
        while (j < sorted.length && Math.max(t0, sorted[j].t.getTime()) <= t) run += sorted[j++].v;
        return run;
      });
      return { ...s, vals };
    });
    const max = niceMax(Math.max(1, ...lines.flatMap((l) => l.vals)));
    return { t0, t1, xs, lines, max };
  }, [series, start, end]);

  const pad = { l: 44, r: 12, t: 10, b: 22 };
  const W = Math.max(0, width);
  const iw = Math.max(1, W - pad.l - pad.r);
  const ih = height - pad.t - pad.b;
  const x = (t) => pad.l + ((t - model.t0) / (model.t1 - model.t0)) * iw;
  const y = (v) => pad.t + ih - (Math.max(0, v) / model.max) * ih;
  const ticks = [0, model.max / 2, model.max];

  const monthTicks = [];
  const d0 = new Date(model.t0);
  for (let d = new Date(d0.getFullYear(), d0.getMonth() + 1, 1); d.getTime() <= model.t1; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    monthTicks.push(d);
  }
  const every = Math.ceil(monthTicks.length / Math.max(1, Math.floor(iw / 48)));

  const path = (vals) =>
    model.xs.map((t, i) => (i === 0 ? `M${x(t)},${y(vals[0])}` : `H${x(t)}V${y(vals[i])}`)).join("");

  function onMove(ev) {
    const r = ev.currentTarget.getBoundingClientRect();
    const t = model.t0 + ((ev.clientX - r.left - pad.l) / iw) * (model.t1 - model.t0);
    // The last change on or before the pointer's day.
    let i = 0;
    while (i + 1 < model.xs.length && model.xs[i + 1] <= t) i++;
    setHover({ i, t: Math.min(Math.max(t, model.t0), model.t1) });
  }

  const ends = model.lines.map((l) => ({ l, v: l.vals[l.vals.length - 1] }));
  const endsCollide = ends.length > 1 && Math.abs(y(ends[0].v) - y(ends[1].v)) < 16;
  const fmtDay = (t) => new Date(t).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

  if (asTable) {
    return (
      <>
        <TableToggle asTable onToggle={() => setAsTable(false)} />
        <table className="w-full text-sm">
          <caption className="sr-only">Running total</caption>
          <thead>
            <tr className="text-[11px] text-birdie-charcoal/50 text-left">
              <th className="font-medium py-1">Date</th>
              {model.lines.map((l) => (
                <th key={l.key} className="font-medium py-1 text-right">{l.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.xs.slice(1, -1).map((t, k) => (
              <tr key={t} className="border-t border-birdie-green/10">
                <td className="py-1.5 text-birdie-charcoal/70">{fmtDay(t)}</td>
                {model.lines.map((l) => (
                  <td key={l.key} className="py-1.5 text-right tabular-nums">{formatMoney(l.vals[k + 1])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <>
      <TableToggle asTable={false} onToggle={() => setAsTable(true)} />
      {model.lines.length > 1 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-1 text-[11px] text-birdie-charcoal/60">
          {ends.map(({ l, v }) => (
            <span key={l.key} className="flex items-center gap-1.5">
              <span className="h-0.5 w-3 rounded-full" style={{ background: l.color }} />
              {l.label} <span className="font-semibold text-birdie-charcoal tabular-nums">{formatMoney(v)}</span>
            </span>
          ))}
        </div>
      )}
      <div ref={ref} className="relative" style={{ height }}>
        {W > 0 && (
          <svg
            width={W}
            height={height}
            role="img"
            aria-label={`Running total: ${ends.map((e) => `${e.l.label} ${formatMoney(e.v)}`).join(", ")}`}
            onPointerMove={onMove}
            onPointerDown={onMove}
            onPointerLeave={() => setHover(null)}
            className="block touch-pan-y"
          >
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={INK} strokeOpacity="0.1" />
                <text x={pad.l - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize="10" fill={INK} fillOpacity="0.5" className="tabular-nums">
                  {compact(t)}
                </text>
              </g>
            ))}
            {monthTicks.map((d, k) =>
              k % every ? null : (
                <text key={d.getTime()} x={x(d.getTime())} y={height - 6} textAnchor="middle" fontSize="10" fill={INK} fillOpacity="0.5">
                  {d.toLocaleDateString([], { month: "short" })}
                </text>
              )
            )}
            {model.lines.length === 1 && (
              <path
                d={`${path(model.lines[0].vals)}V${y(0)}H${x(model.xs[0])}Z`}
                fill={model.lines[0].color}
                fillOpacity="0.1"
              />
            )}
            {[...model.lines].reverse().map((l) => (
              <path key={l.key} d={path(l.vals)} fill="none" stroke={l.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            ))}
            {[...ends].reverse().map(({ l, v }) => (
              <circle key={l.key} cx={x(model.t1)} cy={y(v)} r="4" fill={l.color} stroke="#fff" strokeWidth="2" />
            ))}
            {!endsCollide &&
              hover === null &&
              ends.map(({ l, v }) => (
                <text
                  key={l.key}
                  x={x(model.t1) - 8}
                  y={y(v) - 8 < 12 ? y(v) + 18 : y(v) - 8}
                  textAnchor="end"
                  fontSize="11"
                  fontWeight="600"
                  fill={INK}
                >
                  {compact(v)}
                </text>
              ))}
            {hover && (
              <g pointerEvents="none">
                <line x1={x(hover.t)} x2={x(hover.t)} y1={pad.t} y2={pad.t + ih} stroke={INK} strokeOpacity="0.25" />
                {[...model.lines].reverse().map((l) => (
                  <circle key={l.key} cx={x(hover.t)} cy={y(l.vals[hover.i])} r="4" fill={l.color} stroke="#fff" strokeWidth="2" />
                ))}
              </g>
            )}
          </svg>
        )}
        {hover && (
          <div
            className="absolute top-0 z-10 rounded-lg bg-birdie-charcoal text-white text-[11px] px-2 py-1.5 shadow-lg pointer-events-none whitespace-nowrap"
            style={x(hover.t) > W / 2 ? { right: W - x(hover.t) + 8 } : { left: x(hover.t) + 8 }}
          >
            {model.lines.map((l) => (
              <div key={l.key} className="flex items-center gap-1.5">
                <span className="h-0.5 w-2.5 rounded-full" style={{ background: l.color }} />
                <span className="font-semibold">{formatMoney(l.vals[hover.i])}</span>
                <span className="opacity-70">{l.label}</span>
              </div>
            ))}
            <div className="opacity-70">by {fmtDay(hover.t)}</div>
          </div>
        )}
      </div>
    </>
  );
}

// Horizontal bars: the label and value sit on the row, so nothing hides in a tooltip.
function BarList({ rows, total, label, active, onPick, showCount, footer }) {
  const [all, setAll] = useState(false);
  if (!rows.length) return <p className="text-sm text-birdie-charcoal/50 py-4 text-center">Nothing in this range.</p>;
  const max = Math.max(1, ...rows.map((r) => r.total));
  const shown = all ? rows : rows.slice(0, 8);
  return (
    <div>
      <ul className="space-y-1">
        {shown.map((r) => {
          const on = active === r.key;
          const inner = (
            <>
              <div className="flex items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-birdie-charcoal">{label(r)}</span>
                {showCount && <span className="text-[11px] text-birdie-charcoal/45">{r.count}×</span>}
                <span className="text-[11px] text-birdie-charcoal/45 tabular-nums w-9 text-right">{pct(r.total, total)}</span>
                <span className="w-20 text-right tabular-nums font-medium text-birdie-charcoal">{usd0(r.total)}</span>
              </div>
              <div className="h-1.5 mt-1 rounded-r bg-birdie-cream">
                <div
                  className="h-full rounded-r"
                  style={{
                    width: `${Math.max(1.5, (Math.max(0, r.total) / max) * 100)}%`,
                    background: active && !on ? REST : MARK,
                  }}
                />
              </div>
            </>
          );
          return (
            <li key={r.key}>
              {onPick ? (
                <button
                  type="button"
                  onClick={() => onPick(r.key)}
                  aria-pressed={on}
                  className={`w-full text-left rounded-lg px-2 py-1.5 -mx-2 ${on ? "bg-birdie-green/10" : "hover:bg-birdie-cream/70"}`}
                >
                  {inner}
                </button>
              ) : (
                <div className="py-1.5">{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
      {rows.length > 8 && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1 text-xs font-semibold text-birdie-green">
          {all ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
      {footer}
    </div>
  );
}
